import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { Theme, ThemeColors, getActiveTheme } from '../api/client'

interface ThemeContextType {
  theme: Theme | null
  isLoading: boolean
  error: string | null
  applyTheme: (theme: Theme) => void
  refreshTheme: () => Promise<void>
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

// Default theme colors (matching the Default theme in backend)
const DEFAULT_COLORS: ThemeColors = {
  primary: '#3B82F6',
  primary_hover: '#2563EB',
  primary_light: '#DBEAFE',
  secondary: '#6B7280',
  accent: '#8B5CF6',
  background: '#F9FAFB',
  surface: '#FFFFFF',
  sidebar_bg: '#FFFFFF',
  sidebar_border: '#E5E7EB',
  text_primary: '#111827',
  text_secondary: '#6B7280',
  text_on_primary: '#FFFFFF',
  border: '#E5E7EB',
  success: '#10B981',
  warning: '#F59E0B',
  error: '#EF4444',
  info: '#3B82F6'
}

function applyColorsToDocument(colors: ThemeColors): void {
  const root = document.documentElement

  root.style.setProperty('--color-primary', colors.primary)
  root.style.setProperty('--color-primary-hover', colors.primary_hover)
  root.style.setProperty('--color-primary-light', colors.primary_light)
  root.style.setProperty('--color-secondary', colors.secondary)
  root.style.setProperty('--color-accent', colors.accent)
  root.style.setProperty('--color-background', colors.background)
  root.style.setProperty('--color-surface', colors.surface)
  root.style.setProperty('--color-sidebar-bg', colors.sidebar_bg)
  root.style.setProperty('--color-sidebar-border', colors.sidebar_border)
  root.style.setProperty('--color-text-primary', colors.text_primary)
  root.style.setProperty('--color-text-secondary', colors.text_secondary)
  root.style.setProperty('--color-text-on-primary', colors.text_on_primary)
  root.style.setProperty('--color-border', colors.border)
  root.style.setProperty('--color-success', colors.success)
  root.style.setProperty('--color-warning', colors.warning)
  root.style.setProperty('--color-error', colors.error)
  root.style.setProperty('--color-info', colors.info)
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const applyTheme = useCallback((newTheme: Theme) => {
    setTheme(newTheme)
    applyColorsToDocument(newTheme.colors)

    // Apply custom CSS if present
    let customStyleElement = document.getElementById('theme-custom-css')
    if (newTheme.custom_css) {
      if (!customStyleElement) {
        customStyleElement = document.createElement('style')
        customStyleElement.id = 'theme-custom-css'
        document.head.appendChild(customStyleElement)
      }
      customStyleElement.textContent = newTheme.custom_css
    } else if (customStyleElement) {
      customStyleElement.remove()
    }
  }, [])

  const refreshTheme = useCallback(async () => {
    try {
      const activeTheme = await getActiveTheme()
      applyTheme(activeTheme)
      setError(null)
    } catch (err: any) {
      console.error('Failed to fetch active theme:', err)
      // Apply default colors on error
      applyColorsToDocument(DEFAULT_COLORS)
      setError(err.response?.data?.detail || 'Failed to load theme')
    }
  }, [applyTheme])

  useEffect(() => {
    // Disable transitions during initial load to prevent flash
    document.body.classList.add('no-transitions')

    // Apply default colors immediately
    applyColorsToDocument(DEFAULT_COLORS)

    // Fetch active theme from API with retry logic
    const fetchTheme = async (retryCount = 0, maxRetries = 5) => {
      try {
        const activeTheme = await getActiveTheme()
        applyTheme(activeTheme)
        setIsLoading(false)
        // Re-enable transitions after a short delay
        requestAnimationFrame(() => {
          document.body.classList.remove('no-transitions')
        })
      } catch (err: any) {
        console.error(`Failed to fetch active theme (attempt ${retryCount + 1}):`, err)

        // Retry with exponential backoff if we haven't exceeded max retries
        if (retryCount < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, retryCount), 10000) // Max 10 seconds
          console.log(`Retrying theme fetch in ${delay}ms...`)
          setTimeout(() => fetchTheme(retryCount + 1, maxRetries), delay)
        } else {
          // Max retries exceeded, keep default colors
          setError(err.response?.data?.detail || 'Failed to load theme')
          setIsLoading(false)
          requestAnimationFrame(() => {
            document.body.classList.remove('no-transitions')
          })
        }
      }
    }

    fetchTheme()
  }, [applyTheme])

  return (
    <ThemeContext.Provider value={{ theme, isLoading, error, applyTheme, refreshTheme }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme(): ThemeContextType {
  const context = useContext(ThemeContext)
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

// Export for external use
export { DEFAULT_COLORS }
